class IstoSwiper extends HTMLElement {
  constructor() {
    super();

    this.swiperId = this.id;
    this.dataSelector = this.getAttribute("data-selector");
    this.total = this.getAttribute("data-total");
    const loadingElem = this.getAttribute("data-loading-elem");
    this.dataTargetSelector = loadingElem;
    this.loadingDiv = document.querySelector(`.${loadingElem}`);
    this.swiperDiv = document.querySelector(`.${this.dataSelector}`);
    this.ignoreLazyLoading = this.shouldIgnoreLazyLoad();
    this.layoutConstraint = this.getAttribute("data-layout") === "true";

    // Swiper configuration
    this.swiperInstance;
  }

  shouldIgnoreLazyLoad() {
    let dLazy = this.getAttribute("data-lazy");
    if (dLazy === null || dLazy === undefined || dLazy === "") {
      return true;
    } else {
      return !dLazy;
    }
  }

  connectedCallback() {
    if (this.ignoreLazyLoading) {
      if (document.readyState === "complete") {
        // If already loaded, initialize immediately
        this.swiperInstance = new Swiper(
          `.${this.dataSelector}`,
          this.getSwiperOptions()
        );
      } else {
        // Otherwise, wait for the load event
        window.addEventListener("load", () => {
          this.swiperInstance = new Swiper(
            `.${this.dataSelector}`,
            this.getSwiperOptions()
          );
        });
      }
    } else {
      this.initSwiper();
    }
  }

  initSwiper() {
    // let swiperInstance;

    const initSwiperInstance = (selector) => {
      const s = document.querySelector(selector);

      if (!s) return; // Exit if the selector doesn't exist

      // Cleanup existing Swiper instance
      if (this.swiperInstance) {
        this.swiperInstance.destroy(true, true);
      }

      // Initialize new Swiper instance
      this.swiperInstance = new Swiper(selector, this.getSwiperOptions());
    };

    // Function to create an intersection observer
    const createObserverInstance = (targetSelector, selector) => {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(
          (entry) => {
            if (entry.isIntersecting) {
              console.debug("Creating instance for swiper...");
              initSwiperInstance(`.${selector}`);
              // Stop observing once initialized
              observer.unobserve(entry.target);
            }
          },
          {
            rootMargin: "100px 0px 0px 0px", // Trigger when the top of the element is in the upper half of the viewport
            threshold: 0,
          }
        );
      });

      // Start observing the specified element
      const targetElement = document.querySelector([`.${targetSelector}`]);
      if (targetElement) {
        observer.observe(targetElement);
      }
    };

    // Ensure Swiper initializes on page load
    if (document.readyState === "complete") {
      // If already loaded, initialize immediately
      createObserverInstance(this.dataTargetSelector, this.dataSelector);
    } else {
      // Otherwise, wait for the load event
      window.addEventListener("load", () => {
        createObserverInstance(this.dataTargetSelector, this.dataSelector);
      });
    }

    // Reinitialize Swiper when a section is loaded or changed in the theme editor
    document.addEventListener("shopify:section:load", (event) => {
      if (event.detail.sectionId === this.id) {
        createObserverInstance(this.dataTargetSelector, this.selector);
      }
    });

    // Cleanup Swiper instance when the section is unloaded
    document.addEventListener("shopify:section:unload", (event) => {
      if (event.detail.sectionId === this.id) {
        if (this.swiperInstance) {
          this.swiperInstance.destroy(true, true);
          this.swiperInstance = null;
        }
      }
    });
  }

  getSwiperOptions = () => {
    const slidesPerView = this.getSlidesPerView();
    const spaceBetween = this.getSpaceBetween();
    const slidesBeforeAndAfter = this.getSlidesBeforeAndAfter();

    return {

      slidesPerView: slidesPerView.mobile,
      spaceBetween: spaceBetween.mobile,
      ...this.getMousewheelOptions(),
      ...this.getNavigationOptions(),
      ...this.getScrollbar(),
      ...this.getPaginationOptions(),
      ...this.getFreeModeOptions(),
      ...this.getLoopOptions(),
      ...this.getKeyboardOptions(),
      ...this.getCentered(),
      slidesOffsetAfter: slidesBeforeAndAfter.mobile.slidesOffsetAfter,
      slidesOffsetBefore: slidesBeforeAndAfter.mobile.slidesOffsetBefore,
      grabCursor: true,
      breakpoints: {
        750: {
          slidesPerView: slidesPerView.tablet,
          spaceBetween: spaceBetween.tablet,
          slidesOffsetAfter: slidesBeforeAndAfter.tablet.slidesOffsetAfter,
          slidesOffsetBefore: slidesBeforeAndAfter.tablet.slidesOffsetBefore,
        },
        990: {
          slidesPerView: slidesPerView.desktop,
          spaceBetween: spaceBetween.desktop,
          slidesOffsetAfter: slidesBeforeAndAfter.desktop.slidesOffsetAfter,
          slidesOffsetBefore: slidesBeforeAndAfter.desktop.slidesOffsetBefore,
        },
      },
      on: {
        init: () => {
          console.debug("Swiper Initialized.");
          this.loadingDiv.setAttribute("data-loaded", "true");
          this.swiperDiv.setAttribute("data-loading", "false");
        },
      },
    };
  };

  getScrollbar = () => {
    const elem = this.getAttribute("data-scrollbar-elem");

    if (!elem) {
      return {};
    }

    return {
      scrollbar: {
        el: elem,
        dragClass: "swiper-scrollbar-drag-s",
        hide: false,
        draggable: true,
      },
    };
  };

  getSpaceBetween = () => {
    const mobileSpaceBetween =
      this.getAttribute("data-mobile-space-between") ?? "20px";
    const tabletSpaceBetween =
      this.getAttribute("data-tablet-space-between") ?? "20px";
    const desktopSpaceBetween =
      this.getAttribute("data-space-between") ?? "8px";

    return {
      mobile: mobileSpaceBetween,
      tablet: tabletSpaceBetween,
      desktop: desktopSpaceBetween,
    };
  };

  getCentered = () => {
    const centered = this.getAttribute("data-centered");

    if (centered === "true") {
      return {
        centeredSlides: true,
      };
    }

    return {};
  };

  getSlidesPerView = () => {
    const mobileSlidesPerView =
      this.getAttribute("data-mobile-slides-per-view") ?? 2.3;
    const tabletSlidesPerView =
      this.getAttribute("data-tablet-slides-per-view") ?? 3.7;
    const desktopSlidesPerView =
      this.getAttribute("data-slides-per-view") ?? 4.2;

    return {
      mobile: mobileSlidesPerView,
      tablet: tabletSlidesPerView,
      desktop: desktopSlidesPerView,
    };
  };

  getNavigationOptions = () => {
    const nextElem = this.getAttribute("data-navigation-btn-next");
    const prevElem = this.getAttribute("data-navigation-btn-prev");

    if (nextElem && prevElem) {
      return {
        navigation: {
          nextEl: nextElem,
          prevEl: prevElem,
        },
      };
    }

    return {};
  };

  getPaginationOptions = () => {
    const mwElem = this.getAttribute("data-pagination-elem");
    if (mwElem) {
      return {
        pagination: {
          el: mwElem,
          dynamicBullets:
            this.getAttribute("data-dynamic-bullets") == "true" ? true : false,
        },
      };
    }

    return {};
  };

  getFreeModeOptions = () => {
    const mwElem = this.getAttribute("data-free-mode");

    if (mwElem === "true") {
      return {
        freeMode: true,
      };
    }

    return {};
  };

  getLoopOptions = () => {
    const mwElem = this.getAttribute("data-loop");

    if (mwElem === "true") {
      return {
        loop: true,
      };
    }

    return {};
  };

  getKeyboardOptions = () => {
    const mwElem = this.getAttribute("data-keyboard-control");

    if (mwElem === "true") {
      return {
        keyboard: {
          enabled: true,
        },
      };
    }

    return {};
  };

  getMousewheelOptions = () => {
    const mwElem = this.getAttribute("data-mousewheel");

    if (mwElem === "true") {
      return {
        mousewheel: {
          forceToAxis: true,
        },
      };
    }

    return {};
  };

  getSlidesBeforeAndAfter() {

    const breakpoints = ["mobile", "tablet", "desktop"];
    if(this.layoutConstraint) {
      return breakpoints.reduce((acc, bp) => {
        acc[bp] = {
          slidesOffsetBefore: 0,
          slidesOffsetAfter: 0,
        };
        return acc;
      }, {});
    }

    const slidesPerView = this.getSlidesPerView();
    const defaultOffsetMob = this.remToPx(0);
    const defaultOffset = this.remToPx(0);

    const offsets = breakpoints.reduce((acc, bp) => {
      const offset = bp === "mobile" ? defaultOffsetMob : defaultOffset;
      acc[bp] = {
        slidesOffsetBefore: this.total === slidesPerView[bp] ? 0 : offset,
        slidesOffsetAfter: this.total === slidesPerView[bp] ? 0 : offset,
      };
      return acc;
    }, {});

    return offsets;
  }

  remToPx(rem) {
    const rootFontSize = parseFloat(
      getComputedStyle(document.documentElement).fontSize
    );
    return rem * rootFontSize;
  }
}

customElements.define("isto-swiper", IstoSwiper);
